// Parses CopilotKit / fetch error responses to detect rate-limit and
// tier-paywall conditions. parseQuotaError is exported for unit testing;
// the hook integration (subscribing to CopilotKit error events) is handled
// in CopilotProvider.
//
// Wire shape note: the backend's http_exc_handler (api/src/.../app.py)
// returns the HTTPException's `detail` dict directly — no FastAPI-default
// {"detail": ...} wrapper. We accept both shapes so the parser is robust
// to handler changes and to any future runtime that re-wraps the body.

export interface QuotaError {
  kind:
    | "tier_required"
    | "messages_exhausted"
    | "burst_exceeded"
    | "cost_safety_cap"
    | "service_unavailable";
}

export interface ErrorLike {
  status?: number;
  body?: unknown;
}

const RATE_LIMIT_KINDS = new Set([
  "tier_required",
  "messages_exhausted",
  "burst_exceeded",
  "cost_safety_cap",
]);

export function parseQuotaError(err: ErrorLike): QuotaError | null {
  // Network failure / unknown → service_unavailable so the user sees something
  if (err.status === undefined && err.body === undefined) {
    return { kind: "service_unavailable" };
  }

  const raw = err.body as
    | { error?: string; kind?: string; detail?: { error?: string; kind?: string } }
    | undefined;
  const payload = raw?.detail ?? raw;
  if (!payload) return null;

  // 429 → rate-limited (burst / daily messages / cost cap)
  if (err.status === 429 && payload.error === "rate_limited") {
    const kind = payload.kind;
    if (kind && RATE_LIMIT_KINDS.has(kind)) {
      return { kind: kind as QuotaError["kind"] };
    }
    return null;
  }

  // 402 → free-tier user hit a Pro/Analyst-only endpoint (defense-in-depth;
  // AskTapelensButton normally gates this client-side, but we still surface
  // the upgrade prompt if the request slips through).
  if (err.status === 402 && payload.error === "tier_required") {
    return { kind: "tier_required" };
  }

  return null;
}
